import type { Graph } from './graph.js'

/** Шаг между слоями по горизонтали. Карточка узла — 12rem при 16 px. */
export const ШАГ_X = 220
/** Шаг между узлами одного слоя по вертикали. */
export const ШАГ_Y = 120
/** Сколько слоёв помещается в строку, прежде чем начнётся перенос. */
export const СЛОЁВ_В_СТРОКЕ = 5
/** Отступ новой строки вниз. Больше ШАГ_Y: строки должны различаться глазом. */
export const ШАГ_СТРОКИ = 320
/** Левый верхний угол раскладки. Тот же, что у первого узла из палитры. */
export const НАЧАЛО = { x: 80, y: 80 } as const

export interface Место {
  x: number
  y: number
}

/**
 * Глубина каждого узла: на единицу больше самого глубокого из его входов.
 *
 * Обход в ширину от узлов без входящих связей. Обратные связи глубину не
 * увеличивают — иначе на цикле обход не сошёлся бы, а цикл в движке
 * законен: узел срабатывает повторно, когда на все его входы пришли новые
 * данные.
 *
 * Если узлов без входов нет вовсе (граф целиком в цикле), обход начинается
 * с первого узла графа: раскладка обязана получиться при любом графе.
 */
function глубины(graph: Graph): Map<string, number> {
  const исходящие = new Map<string, string[]>()
  const входящих = new Map<string, number>()
  const существует = new Set(graph.nodes.map((n) => n.id))

  for (const node of graph.nodes) входящих.set(node.id, 0)

  for (const c of graph.connections) {
    // Висячая связь возможна: граф правят руками и переносят JSON-ом.
    if (!существует.has(c.from.node) || !существует.has(c.to.node)) continue
    исходящие.set(c.from.node, [...(исходящие.get(c.from.node) ?? []), c.to.node])
    входящих.set(c.to.node, (входящих.get(c.to.node) ?? 0) + 1)
  }

  const глубина = new Map<string, number>()
  const очередь = graph.nodes.filter((n) => (входящих.get(n.id) ?? 0) === 0).map((n) => n.id)
  const начало = очередь.length > 0 ? очередь : graph.nodes.slice(0, 1).map((n) => n.id)

  for (const id of начало) глубина.set(id, 0)

  /*
   * Потолок глубины. В графе без циклов слоёв не больше, чем узлов, —
   * значит всё, что глубже, порождено обратной связью. Без потолка обход
   * на цикле не сходится вовсе: каждый оборот увеличивал бы глубину, и
   * «глубже побеждает» крутилось бы вечно (поймано проверкой на цикле —
   * она просто не завершалась).
   */
  const потолок = Math.max(graph.nodes.length - 1, 0)

  const ждут = [...начало]
  while (ждут.length > 0) {
    const id = ждут.shift() as string
    const своя = глубина.get(id) ?? 0
    if (своя >= потолок) continue

    for (const сосед of исходящие.get(id) ?? []) {
      const была = глубина.get(сосед)
      // Узел ложится глубже самого глубокого из входов: короткая ветвь
      // не должна тянуть слияние влево, к себе.
      if (была !== undefined && была >= своя + 1) continue
      глубина.set(сосед, своя + 1)
      ждут.push(сосед)
    }
  }

  // Узлы, до которых обход не добрался (отдельный цикл в стороне),
  // кладутся в нулевой слой: место обязан получить каждый.
  for (const node of graph.nodes) if (!глубина.has(node.id)) глубина.set(node.id, 0)

  return глубина
}

/**
 * Раскладка графа по связям.
 *
 * Чистая функция: ни React, ни библиотека холста ей не нужны, поэтому
 * она проверяется без браузера. Применяется по команде автора, а не сама
 * (FR-UI-012): расставленный руками граф — это работа, и стирать её при
 * каждой правке нельзя.
 */
export function layout(graph: Graph): Map<string, Место> {
  const места = new Map<string, Место>()
  const глубина = глубины(graph)

  /*
   * Узлы по слоям. Порядок внутри слоя — порядок узлов в самом графе:
   * он устойчив между вызовами, а значит повторное нажатие «Разложить»
   * не двигает граф без причины.
   */
  const слои = new Map<number, string[]>()
  for (const node of graph.nodes) {
    const d = глубина.get(node.id) ?? 0
    слои.set(d, [...(слои.get(d) ?? []), node.id])
  }

  for (const [d, узлы] of слои) {
    // Перенос строкой: семь узлов в одну линию не читаются ни при какой
    // ширине окна, потому что показ целиком уводит масштаб ниже порога
    // чтения имени.
    const строка = Math.floor(d / СЛОЁВ_В_СТРОКЕ)
    const столбец = d % СЛОЁВ_В_СТРОКЕ

    узлы.forEach((id, индекс) => {
      места.set(id, {
        x: НАЧАЛО.x + столбец * ШАГ_X,
        y: НАЧАЛО.y + строка * ШАГ_СТРОКИ + индекс * ШАГ_Y,
      })
    })
  }

  return места
}
